import {
  createContext,
  useState,
  useContext,
  ReactNode,
  Context,
  useEffect,
  useRef,
} from "react";
import environment from "../../relayEnvironment"; // Adjust path to your Relay environment setup
import { commitMutation, graphql, useLazyLoadQuery } from "react-relay";
import { Procedure, SurgeonProcedure } from "../../types/procedures";
import { Surgeon } from "../../types/surgeons";
import { ProcedureType } from "../../types/procedure_type";
import { BusinessLocation } from "../../types/business_location";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { trackQuoteCompletedOnce } from "../../utils/googleAds";
import {
  readQuoteResumeToken,
  storeQuoteResumeToken,
} from "../../utils/quoteResumeToken";
import { abTestingService } from "../../utils/abTestingService";
import { useTranslation } from "react-i18next";
import {
  LeadOriginHistoryEntry,
  TrackingIds,
  extractTrackingIdsFromSearchParams,
  getStoredValue,
  mergeTrackingIds,
  persistTrackingFromUrl,
  readLeadOriginHistoryCookie,
  readTrackingIdsCookie,
  resolveLeadOrigin,
} from "../../utils/tracking";
import {
  appendTouch,
  buildTouch,
  readTouches,
  TrackingTouch,
} from "../../utils/touchHistory";
import { initialLanding } from "../../utils/initialLanding";
import { usePatienAuthProvider } from "../patient-portal/authentication/usePatientProvider";
import { setEventContext } from "../../utils/clientEvents";
import { executeFunnelRecaptcha } from "../../utils/funnelRecaptcha";
import {
  calculateQuotePricing,
  computeAutoModifierPercentage,
  DISABLE_PRICE_MODIFIERS_FLAG,
  groupPricingForPatientDisplay,
  LOCAL_ANESTHESIA_ELIGIBLE_FLAG,
  SPECIAL_FLAG,
  type PatientDisplayRow,
  type QuotePricing,
} from "@shared/pricing/calculateQuotePricing";
import { isWithinActiveWindow } from "@shared/pricing/activeWindow";
import { filterSpecialShadowed } from "@shared/pricing/specialShadow";

export type LocationOption = {
  label: string;
  value: string;
  image: string;
};

type SurgeonPricing = {
  minPrice: number | undefined;
  maxPrice: number | undefined;
};

export type ProcedureTypePriceRange = {
  procedureType: ProcedureType;
  minPrice: number | undefined;
  maxPrice: number | undefined;
};

type ProceduresPricing = {
  minPrice: number | undefined;
  maxPrice: number | undefined;
};

type Gender = "FEMALE" | "MALE" | "OTHER" | null;

export type QuoteAddOn = "SEVEN_DAY_PACKAGE" | "THREE_DAY_PACKAGE" | "ACCESSORIES";

export type PromoCodePayload = {
  id: string;
  code: string;
  target: "PROCEDURE_PRICE" | "BOOKING_PRICE";
  amountOffCents: number;
  validFrom: string;
  validUntil: string;
};

// Friends & Family Pass sessionStorage stash key — module-scoped so any
// helper that clears/reads it stays correct if extracted from the provider.
const GIFT_PASS_STASH_KEY = "gift_pass_code";

type Quote = {
  quoteId: string;
  quoteStatus: string | null;
  isLoading: boolean;
  draftQuoteId: string;
  selectedSurgeon: Surgeon | undefined;
  selectedProcedureTypes: ProcedureType[];
  selectedProcedures: Procedure[];
  addSelectedProcedure: (procedure: Procedure) => void;
  removeSelectedProcedure: (procedure: Procedure) => void;
  gender: Gender;
  setGender: (gender: Gender) => void;
  email: string;
  setEmail: (email: string | "") => void;
  zipCode: string;
  setZipCode: (zipCode: string | "") => void;
  phone: string;
  setPhone: (phone: string | "") => void;
  name: string;
  setName: (name: string | "") => void;
  lastName: string;
  setLastName: (name: string | "") => void;
  age: any;
  setAge: any;
  weight: any;
  setWeight: any;
  heightFt: any;
  heightIn: any;
  setHeightFt: (height: string | "") => void;
  setHeightIn: (height: string | "") => void;
  hasMedicalConditions: boolean;
  setHasMedicalConditions: any;
  medicalConditions: any;
  setMedicalConditions: any;
  smoker: boolean | undefined;
  setSmoker: any;
  priorCosmeticSurgery: any;
  setPriorCosmeticSurgery: any;
  lost50pounds: any;
  setLost50Pounds: any;
  patientReadiness: string;
  setPatientReadiness: (value: string) => void;
  surgeryDateRange: string;
  setSurgeryDateRange: (value: string) => void;
  budget: string;
  setBudget: (value: string) => void;
  willingToTravel: string;
  setWillingToTravel: (value: string) => void;
  patientNotes: string;
  setPatientNotes: (value: string) => void;
  location?: string;
  setLocation: any;
  locationOptions: LocationOption[];
  procedures: any;
  proceduresAllGender: any;
  procedureTypes: ProcedureType[];
  surgeons: any;
  getSurgeonPricing: () => SurgeonPricing;
  getQuotePricing: () => QuotePricing;
  getQuotePricingWithPhotos: () => QuotePricing;
  getPatientDisplayRows: () => PatientDisplayRow[];
  getSelectedProcedureTypePriceRanges: () => ProcedureTypePriceRange[];
  getAvailableSurgeons: () => Surgeon[];
  setSelectedProcedureTypes: (procedureTypes: ProcedureType[]) => void;
  clearSelectedProcedures: () => void;
  updateSurgeon: (id: string) => void;
  submitPersonalizedQuote: (options?: SubmitQuoteOptions) => void;
  startDraftQuote: (options?: StartDraftQuoteOptions) => Promise<string | null>;
  fileList: any;
  setFileList: any;
  cherryFinancingFile: any;
  setCherryFinancingFile: any;
  saveFilesToQuote: (updatedFileList: any[], updatedCherryFile?: any) => void;
  markFinancingApplyClicked: (provider: string) => void;
  markBookNowPressed: () => void;
  markEvaluationRequested: () => void;

  // Personalization
  addOns: QuoteAddOn[];
  setAddOns: (value: QuoteAddOn[]) => void;
  toggleAddOn: (value: QuoteAddOn) => void;

  //skipped personal details
  skippedPersonalDetails: boolean;
  setSkippedPersonalDetails: (value: boolean) => void;

  //hipaa agreement
  hipaaAgreement: boolean;
  setHipaaAgreement: (value: boolean) => void;

  //description
  procedureDescription: string;
  setProcedureDescription: (value: string) => void;
  otherMedicalConditionDescription: string;
  setOtherMedicalConditionDescription: (value: string) => void;
  priorCosmeticSurgeryDescription: string;
  setPriorCosmeticSurgeryDescription: (value: string) => void;

  //simple medical history
  simpleMedicalHistory: string;
  setSimpleMedicalHistory: (value: string) => void;

  //did we reload the query from id
  quoteReloaded: boolean;
  setQuoteReloaded: (value: boolean) => void;

  //did we reload the query from url params
  quoteShared: boolean;
  setQuoteShared: (value: boolean) => void;

  optInText: boolean;
  setOptInText: (value: boolean) => void;

  // Promo code (shared across steps)
  appliedPromoCode: PromoCodePayload | null;
  setAppliedPromoCode: (promo: PromoCodePayload | null) => void;

  // querystring params
  leadOrigin?: string | null;
  trackingIds?: TrackingIds | null;
  leadOriginFirst?: string | null;
  leadOriginLast?: string | null;
  leadOriginHistory?: LeadOriginHistoryEntry[] | null;

  // Friends & Family Pass. Set from the ?gift= URL param on mount;
  // persisted in sessionStorage so step-navigation that strips the query
  // doesn't lose the code. null = no active pass.
  giftPass: { code: string; giverFirstName: string; expiresAt: string } | null;
  // Reason the pass is dead — shown in the gentle banner variant.
  giftPassDead: "redeemed" | "expired" | null;
  // Clears the pass on the client (e.g. after a 400 from create-booking-invoice).
  clearGiftPass: () => void;
};

type SubmitQuoteOptions = {
  sendSuccessEmail?: boolean;
  redirectOnComplete?: boolean;
};

type StartDraftQuoteOptions = {
  lastStep?: string;
};

// TODO: These modifiers are currently not used in pricing calculations
export const MODIFIER_AGE_OVER_50 = 0.5;
export const MODIFIER_MEDICAL_CONDITION = 0;
export const MODIFIER_SMOKE = 0;
export const MODIFIER_PRIOR_COSMETIC_SURGERY = 0;
export const MODIFIER_LOST_MORE_THAN_50 = 0;

const QuoteContext: Context<Quote | undefined> = createContext<
  Quote | undefined
>(undefined);

export const useQuote = (): Quote => {
  const context = useContext(QuoteContext);
  if (!context) {
    throw new Error("useQuote must be used within a QuoteProvider");
  }
  return context;
};

interface QuoteProviderProps {
  children: ReactNode;
  initialSelectedLocation?: string | null;
  initialSelectedSurgeon?: string | null;
  initialSelectedProcedures?: string[];
  /** Code from the ?gift= query parameter (page.tsx reads it before the router strips it). */
  initialGiftCode?: string;
}

const ADD_QUOTE_MUTATION = graphql`
  mutation QuoteProviderMutation($input: QuoteInput!) {
    addQuote(input: $input) {
      id
      patient {
        id
      }
    }
  }
`;

const UPDATE_QUOTE_MUTATION = graphql`
  mutation QuoteProviderUpdateMutation($input: UpdateQuoteInput!) {
    updateQuote(input: $input) {
      id
      # Any other fields you want to get back from the Consultation type
    }
  }
`;

const MARK_FINANCING_APPLY_CLICKED_MUTATION = graphql`
  mutation QuoteProviderMarkFinancingApplyClickedMutation(
    $quoteId: ID!
    $provider: String!
  ) {
    markFinancingApplyClicked(quoteId: $quoteId, provider: $provider) {
      id
      financingApplyClickedAt
      financingApplyProvider
    }
  }
`;

const MARK_BOOK_NOW_PRESSED_MUTATION = graphql`
  mutation QuoteProviderMarkBookNowPressedMutation($quoteId: ID!) {
    markBookNowPressed(quoteId: $quoteId) {
      id
      bookNowPressedAt
    }
  }
`;

const MARK_EVALUATION_REQUESTED_MUTATION = graphql`
  mutation QuoteProviderMarkEvaluationRequestedMutation($quoteId: ID!) {
    markEvaluationRequested(quoteId: $quoteId) {
      id
      evaluationRequestedAt
    }
  }
`;

const START_DRAFT_QUOTE_MUTATION = graphql`
  mutation QuoteProviderStartDraftQuoteMutation($input: DraftQuoteInput!) {
    startDraftQuote(input: $input) {
      id
    }
  }
`;

const UPDATE_DRAFT_QUOTE_MUTATION = graphql`
  mutation QuoteProviderUpdateDraftQuoteMutation(
    $input: UpdateDraftQuoteInput!
  ) {
    updateDraftQuote(input: $input) {
      id
    }
  }
`;

const ADDONS = {
  SEVEN_DAY_PACKAGE: 2499,
  THREE_DAY_PACKAGE: 999,
  ACCESSORIES: 500,
};

const QUOTE_VERSION = process.env.REACT_APP_QUOTE_VERSION?.trim() || undefined;

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export const QuoteProvider = ({
  children,
  initialSelectedSurgeon,
  initialSelectedProcedures,
  initialSelectedLocation,
  initialGiftCode,
}: QuoteProviderProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = useQuery();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { isSignedIn, refresh } = usePatienAuthProvider();

  // Support multiple URL patterns:
  // 1. Old: /id/:quote_id (query param)
  // 2. New: /app/quote/:step/:quoteId or /app/quote/resume/:quoteId (path param)
  // 3. Wildcard: Extract from wildcard match (e.g., {*: 'id/699e17c479f0fb97b814d123'})

  let incomingQuoteId =
    params.quoteId || params.quote_id || searchQuery.get("quote_id");

  // If not found in direct params, check if it's in the wildcard match
  if (!incomingQuoteId && params["*"]) {
    const wildcardPath = params["*"];
    // Match patterns like "id/QUOTE_ID" or "resume/QUOTE_ID" or "photos/QUOTE_ID"
    const match = wildcardPath.match(/^(?:id|resume|photos)\/([a-f0-9]+)$/);
    if (match) {
      incomingQuoteId = match[1];
    }
  }

  // Consume the quote resume access token (?qt=<token>) BEFORE the quote(id)
  // query below fires, so relayEnvironment attaches it as the X-Quote-Token
  // header on that first request and the resolver returns the FULL doc (which
  // unblocks financing / Book Now photo gate / discount tier). useState's
  // initializer runs once, synchronously, during the first render — before the
  // useLazyLoadQuery below — mirroring PatientAuthQuery's ?token= handoff. The
  // token rides its OWN storage key + header, never the portal-JWT/Authorization
  // lane (see utils/quoteResumeToken.ts). It is stripped from the URL by the
  // effect below so it isn't left in history or pasted into a shareable link.
  useState(() => {
    const qt = searchParams.get("qt");
    if (qt && incomingQuoteId) {
      storeQuoteResumeToken(incomingQuoteId, qt);
    }
  });

  // Strip the consumed ?qt= token from the URL (it now lives in localStorage).
  // replace:true so the tokenless URL doesn't add a back-button trap. Mirrors
  // PatientAuthQuery.tsx's token-strip effect.
  useEffect(() => {
    if (searchParams.has("qt")) {
      searchParams.delete("qt");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  let query;
  let variables;

  if (incomingQuoteId) {
    query = graphql`
      query QuoteProviderWithQuoteQuery($incomingQuoteId: ID!) {
        surgeons {
          id
          name
          image
          basePrice
          daysUntilAvailable
          nickname
          businessLocations {
            id
            name
          }
        }
        # includeShadowed: true — this funnel needs the raw catalog for its
        # saved-quote/deeplink restoration exemption; per-list, gender-aware
        # shadowing is applied client-side via filterSpecialShadowed below.
        procedures(isInternal: false, isDiscontinued: false, includeShadowed: true) {
          id
          name
          marketingName
          marketingNameEs
          description
          image
          maleImage
          procedureSlug
          price
          flags
          isInternal
          position
          activeStartsAt
          activeEndsAt
          specialBadgeText
          specialBadgeTextEs
          specialBadgeColor
          specialOfProcedureId
          incompatibleProcedures {
            id
            name
          }
          prices {
            id
            price
            surgeon {
              id
              name
            }
          }
          procedureTypes {
            id
            name
            image
            useForSimplifiedFlow
          }
          tagline
          taglineEs
          subtitle
          subtitleEs
          facts {
            label
            labelEs
            value
            valueEs
          }
          beforeAfterImages
          heroImage
          popularity {
            tier
            count
          }
        }
        procedureTypes {
          id
          name
          image
          isInternal
          useForSimplifiedFlow
          position
        }
        businessLocations {
          id
          name
          image
        }
        quote(id: $incomingQuoteId) {
          id
          simpleStatus
          accessTokenStatus
          patient {
            id
          }
          status {
            id
            name
          }
          name
          lastName
          email
          phone
          zipCode
          weight
          heightFt
          heightIn
          age
          acceptedHipaa
          priorCosmeticSurgery
          smoker
          procedureDescription
          patientReadiness
          surgeryDateRange
          budget
          willingToTravel
          patientNotes
          addOns
          medicalConditions
          optInText
          language
          fileList {
            uid
            name
            response {
              url
            }
          }
          cherryFinancingFile {
            uid
            name
            response {
              url
            }
          }
          selectedLocation {
            id
          }
          selectedProceduresV2 {
            id
          }
          selectedProcedureTypes {
            id
          }
          selectedSurgeon {
            id
          }
        }
      }
    `;
    variables = { incomingQuoteId };
  } else {
    query = graphql`
      query QuoteProviderQuery {
        surgeons {
          id
          name
          image
          basePrice
          daysUntilAvailable
          nickname
          businessLocations {
            id
            name
          }
        }
        # includeShadowed: true — this funnel needs the raw catalog for its
        # saved-quote/deeplink restoration exemption; per-list, gender-aware
        # shadowing is applied client-side via filterSpecialShadowed below.
        procedures(isInternal: false, isDiscontinued: false, includeShadowed: true) {
          id
          name
          marketingName
          marketingNameEs
          description
          image
          maleImage
          flags
          isInternal
          procedureSlug
          price
          position
          activeStartsAt
          activeEndsAt
          specialBadgeText
          specialBadgeTextEs
          specialBadgeColor
          specialOfProcedureId
          incompatibleProcedures {
            id
            name
          }
          prices {
            id
            price
            surgeon {
              id
              name
            }
          }
          procedureTypes {
            id
            name
            image
            useForSimplifiedFlow
          }
          tagline
          taglineEs
          subtitle
          subtitleEs
          facts {
            label
            labelEs
            value
            valueEs
          }
          beforeAfterImages
          heroImage
          popularity {
            tier
            count
          }
        }
        procedureTypes {
          id
          name
          image
          isInternal
          useForSimplifiedFlow
          position
        }
        businessLocations {
          id
          name
          image
        }
      }
    `;
    variables = {};
  }

  const data: any = useLazyLoadQuery(query, variables);

  // Resume-link access signal → "Request new link" page.
  //   EXPIRED   — an authentic-but-lapsed token for this quote was presented;
  //               always route to self-serve re-issue (pass the stored token so
  //               the server recovers the quoteId).
  //   ANONYMOUS — a bare/forged/mismatched link (no valid grant). Only route to
  //               re-issue when REACT_APP_QUOTE_LINK_REQUIRE_TOKEN is on (default
  //               off → untokenized links keep degrading to the funnel during
  //               rollout, so nothing already in patients' inboxes breaks until
  //               we flip it). Pass the quoteId (no token to recover) — the
  //               endpoint re-issues to the on-file contact, rate-limited.
  useEffect(() => {
    const status = data?.quote?.accessTokenStatus;
    // NEVER bounce a post-payment return to the expired page. The Stripe
    // booking flow redirects to /id/<id>?booking=success (and ?booking=cancelled)
    // and those return URLs are not all tokenized — without this guard, flipping
    // REACT_APP_QUOTE_LINK_REQUIRE_TOKEN on would send a patient who JUST PAID to
    // "your link expired". The booking-success/cancelled landing is a terminal
    // confirmation (QuoteApp renders BookingSuccessPage) and renders fine on the
    // anonymous stripped quote, exactly as it does today.
    if (searchParams.get("booking")) return;
    if (status === "EXPIRED") {
      const expiredToken = readQuoteResumeToken();
      if (expiredToken) {
        navigate(`/action/quote-expired/${encodeURIComponent(expiredToken)}`, {
          replace: true,
        });
      } else if (incomingQuoteId) {
        navigate(`/action/quote-expired/none?qid=${encodeURIComponent(incomingQuoteId)}`, {
          replace: true,
        });
      }
    } else if (
      status === "ANONYMOUS" &&
      process.env.REACT_APP_QUOTE_LINK_REQUIRE_TOKEN === "true" &&
      incomingQuoteId
    ) {
      navigate(`/action/quote-expired/none?qid=${encodeURIComponent(incomingQuoteId)}`, {
        replace: true,
      });
    }
  }, [data?.quote?.accessTokenStatus, navigate, incomingQuoteId, searchParams]);

  const [gender, setGender] = useState<Gender>("FEMALE");

  const [simpleMedicalHistory, setSimpleMedicalHistory] = useState("");

  const [hipaaAgreement, setHipaaAgreement] = useState(false);

  const [skippedPersonalDetails, setSkippedPersonalDetails] = useState(false);
  const [location, setLocation] = useState(
    data.businessLocations.length === 1 ? data.businessLocations[0].id : "",
  );

  const [quoteId, setQuoteId] = useState("");
  const [quoteStatus, setQuoteStatus] = useState<string | null>(null);
  const [draftQuoteId, setDraftQuoteId] = useState("");
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [age, setAge] = useState("");
  const [weight, setWeight] = useState("");
  const [heightFt, setHeightFt] = useState("");
  const [heightIn, setHeightIn] = useState("");
  const [hasMedicalConditions, setHasMedicalConditions] =
    useState<boolean>(false);
  const [medicalConditions, setMedicalConditions] = useState([]);
  const [smoker, setSmoker] = useState(false);
  const [priorCosmeticSurgery, setPriorCosmeticSurgery] = useState(false);
  const [lost50pounds, setLost50Pounds] = useState(false);
  const [patientReadiness, setPatientReadiness] = useState("");
  const [surgeryDateRange, setSurgeryDateRange] = useState("Just Researching");
  const [budget, setBudget] = useState("");
  const [willingToTravel, setWillingToTravel] = useState("");
  const [patientNotes, setPatientNotes] = useState("");
  const [selectedProcedureTypes, setSelectedProcedureTypesState] = useState<
    ProcedureType[]
  >([]);

  const [selectedProcedures, setSelectedProcedures] = useState<Procedure[]>([]);
  const [selectedSurgeon, setSelectedSurgeon] = useState<Surgeon>();
  const [fileList, setFileList] = useState([]);
  const [cherryFinancingFile, setCherryFinancingFile] = useState<any>(null);

  // Ref to track if we've already loaded quote data for a specific quoteId
  const quoteLoadedRef = useRef<string | null>(null);


  // Personalization
  const [addOns, setAddOns] = useState<QuoteAddOn[]>([]);
  const toggleAddOn = (value: QuoteAddOn) => {
    setAddOns((prev) => {
      // SEVEN_DAY and THREE_DAY are mutually exclusive (one stay package).
      // Toggling either one clears the other; turning a stay on auto-includes
      // ACCESSORIES (matches the legacy boolean coupling).
      if (value === "SEVEN_DAY_PACKAGE") {
        if (prev.includes("SEVEN_DAY_PACKAGE")) {
          return prev.filter((a) => a !== "SEVEN_DAY_PACKAGE");
        }
        return Array.from(
          new Set(
            prev
              .filter((a) => a !== "THREE_DAY_PACKAGE")
              .concat(["SEVEN_DAY_PACKAGE", "ACCESSORIES"]),
          ),
        );
      }
      if (value === "THREE_DAY_PACKAGE") {
        if (prev.includes("THREE_DAY_PACKAGE")) {
          return prev.filter((a) => a !== "THREE_DAY_PACKAGE");
        }
        return Array.from(
          new Set(
            prev
              .filter((a) => a !== "SEVEN_DAY_PACKAGE")
              .concat(["THREE_DAY_PACKAGE", "ACCESSORIES"]),
          ),
        );
      }
      // ACCESSORIES: simple toggle.
      return prev.includes(value)
        ? prev.filter((a) => a !== value)
        : prev.concat(value);
    });
  };

  // Descriptions
  const [procedureDescription, setProcedureDescription] = useState("");
  const [
    otherMedicalConditionDescription,
    setOtherMedicalConditionDescription,
  ] = useState("");
  const [priorCosmeticSurgeryDescription, setPriorCosmeticSurgeryDescription] =
    useState("");

  const [quoteReloaded, setQuoteReloaded] = useState(false);
  const [quoteShared, setQuoteShared] = useState(false);

  const [optInText, setOptInText] = useState(false);

  const searchParamsKey = searchParams.toString();
  const [trackingIds, setTrackingIds] = useState<TrackingIds>(() =>
    mergeTrackingIds(
      readTrackingIdsCookie(),
      getStoredValue("gclid") ? { gclid: getStoredValue("gclid") as string } : undefined,
    ),
  );
  const [leadOriginFirst, setLeadOriginFirst] = useState<string | null>(() =>
    getStoredValue("lead_origin_first"),
  );
  const [leadOriginLast, setLeadOriginLast] = useState<string | null>(
    () => getStoredValue("lead_origin_last") || getStoredValue("lead_origin"),
  );
  const [leadOriginHistory, setLeadOriginHistory] = useState<
    LeadOriginHistoryEntry[]
  >(() => readLeadOriginHistoryCookie());

  useEffect(() => {
    const {
      trackingIds: mergedTrackingIds,
      leadOrigin: resolvedLeadOrigin,
      leadOriginFirst: resolvedLeadOriginFirst,
      leadOriginLast: resolvedLeadOriginLast,
      leadOriginHistory: resolvedLeadOriginHistory,
    } = persistTrackingFromUrl(
      window.location.search,
      document.referrer,
      window.location.hostname,
    );

    setTrackingIds(mergedTrackingIds);
    setLeadOriginHistory(resolvedLeadOriginHistory);

    if (resolvedLeadOriginFirst && !leadOriginFirst) {
      setLeadOriginFirst(resolvedLeadOriginFirst);
    }

    if (!leadOriginLast && resolvedLeadOriginLast) {
      setLeadOriginLast(resolvedLeadOriginLast);
    } else if (!leadOriginLast && resolvedLeadOrigin) {
      setLeadOriginLast(resolvedLeadOrigin);
    }

    // Append a TrackingTouch to the journal, resolved from the touch's OWN
    // landing params so the recorded channel is the true entry point.
    //
    // react-router strips the query off the landing URL (`/app/quote/?utm…` →
    // `/app/quote/select-procedure`) before this effect runs, so prefer the
    // pristine snapshot captured at boot whenever the live URL has already lost
    // its tracking params. Resolve from incoming-only IDs (NOT the merged
    // cookie) so a stale gclid from an earlier session can't relabel an
    // SMS/email touch as google_ads — multi-touch credit is decided at read
    // time by the attribution strategy, not by overwriting the touch here.
    // Per-submit dedupe in touchHistory.ts drops no-op repeats, so this is safe
    // to call on every effect run.
    const liveParams = new URLSearchParams(window.location.search);
    const liveHasTrackingIds =
      Object.keys(extractTrackingIdsFromSearchParams(liveParams)).length > 0;
    const landingSearch = liveHasTrackingIds
      ? window.location.search
      : initialLanding.search;
    const landingPathname = liveHasTrackingIds
      ? window.location.pathname
      : initialLanding.pathname;
    const landingReferrer = liveHasTrackingIds
      ? document.referrer
      : initialLanding.referrer;
    const landingParams = new URLSearchParams(landingSearch);
    const incomingIds = extractTrackingIdsFromSearchParams(landingParams);
    const touchOrigin = resolveLeadOrigin({
      searchParams: landingParams,
      trackingIds: incomingIds,
      referrer: landingReferrer,
      hostname: window.location.hostname,
    });

    if (touchOrigin) {
      const touch = buildTouch({
        surface: "app_quote",
        search: landingSearch,
        pathname: landingPathname,
        referrer: landingReferrer,
        resolvedLeadOrigin: touchOrigin,
      });
      appendTouch(touch);
    }
  }, [leadOriginFirst, leadOriginLast, searchParamsKey]);

  useEffect(() => {
    if (!leadOriginLast) {
      const resolved = resolveLeadOrigin({
        searchParams,
        trackingIds,
        referrer: document.referrer,
        hostname: window.location.hostname,
      });
      if (resolved) {
        setLeadOriginLast(resolved);
      }
    }
  }, [leadOriginLast, searchParams, trackingIds]);

  // Friends & Family Pass state.
  // Code is stashed in sessionStorage so step-navigation that strips the URL
  // query doesn't lose it — mirrors the campaign_focus_params stash pattern.
  const [giftPass, setGiftPass] = useState<{
    code: string;
    giverFirstName: string;
    expiresAt: string;
  } | null>(null);
  const [giftPassDead, setGiftPassDead] = useState<"redeemed" | "expired" | null>(null);

  const clearGiftPass = () => {
    setGiftPass(null);
    try { sessionStorage.removeItem(GIFT_PASS_STASH_KEY); } catch {}
  };

  // Resolve the pass on mount from prop or sessionStorage stash.
  useEffect(() => {
    const code =
      initialGiftCode ||
      (() => {
        try { return sessionStorage.getItem(GIFT_PASS_STASH_KEY) || undefined; } catch { return undefined; }
      })();
    if (!code) return;

    fetch(`${process.env.REACT_APP_API_URL || ""}/api/booking-pass/info?code=${encodeURIComponent(code)}`)
      .then((r) => r.json())
      .then((data: any) => {
        if (data.valid) {
          setGiftPass({ code, giverFirstName: data.giverFirstName, expiresAt: data.expiresAt });
          try { sessionStorage.setItem(GIFT_PASS_STASH_KEY, code); } catch {}
        } else {
          if (data.reason === "redeemed" || data.reason === "expired") {
            setGiftPassDead(data.reason);
          }
          // ANY not-valid verdict (incl. disabled/unknown) clears the stash so
          // a dead code isn't re-queried on every mount; a fresh ?gift= visit
          // re-stashes. Network errors (catch below) keep it — transient.
          try { sessionStorage.removeItem(GIFT_PASS_STASH_KEY); } catch {}
        }
      })
      .catch(() => {
        // Network error — silently nothing, funnel continues normally.
      });
    // NOTE: no eslint-disable here — the react-hooks plugin isn't configured
    // in the prod-build eslint, and a disable-comment naming an unknown rule
    // FAILS the CRA production build (same trap as the realtime #406 hotfix).
  }, []); // run once on mount only

  const leadOrigin = leadOriginLast;

  const gclid = trackingIds.gclid;

  // Promo code (shared across steps)
  const [appliedPromoCode, setAppliedPromoCode] = useState<PromoCodePayload | null>(null);

  const { patient } = usePatienAuthProvider();

  useEffect(() => {
    //patient is signed in
    if (patient) {
      setName(patient.firstName || "");
      setLastName(patient.lastName || "");
      setEmail(patient.email || "");
      setPhone(patient.phone || "");
      setZipCode(patient.zipCode || "");
    }

    //reloading from url params
    if (
      data &&
      initialSelectedProcedures &&
      initialSelectedSurgeon &&
      initialSelectedLocation
    ) {
      // Find the surgeon
      const foundSurgeon = data.surgeons.find(
        (s: Surgeon) => s.id === initialSelectedSurgeon,
      );
      if (foundSurgeon) {
        setSelectedSurgeon(foundSurgeon);
      }

      const foundLocation = data.businessLocations.find(
        (bl: BusinessLocation) => bl.id === initialSelectedLocation,
      );
      if (foundLocation) {
        setLocation(foundLocation.id);
      }

      // Find the procedures
      const foundProcedures = data.procedures.filter((p: Procedure) =>
        initialSelectedProcedures.includes(p.id),
      );
      if (foundProcedures.length > 0) {
        setSelectedProcedures(foundProcedures);
      }

      setQuoteShared(true);
    }
  }, [
    data,
    initialSelectedSurgeon,
    initialSelectedProcedures,
    initialSelectedLocation,
  ]);

  const locationOptions = data.businessLocations.map(
    (item: BusinessLocation) => {
      return {
        label: item.name,
        value: item.id,
        image: item.image,
      };
    },
  );

  // Computes the full pricing breakdown from the shared calculator. The
  // patient flow doesn't carry sales-rep modifiers, so they're omitted —
  // the calculator falls back to 0. Risk-factor modifiers are derived
  // from the in-progress medical-history snapshot using the same rules
  // the server will stamp onto the Quote once submitted.
  const autoModifierPct = computeAutoModifierPercentage({
    age,
    weight,
    heightFt,
    heightIn,
    hasMedicalConditions,
    lost50pounds,
    priorCosmeticSurgery,
    smoker,
  });

  // Builds calculator inputs from the in-progress quote state. Takes
  // `hasUploadedPhotosOverride` because callers like `saveFilesToQuote`
  // need to compute pricing against the *just-uploaded* file list before
  // React state has flushed.
  const buildCalculatorInputs = (hasUploadedPhotosOverride?: boolean) => {
    const procedures = selectedProcedures.map((p: Procedure) => {
      const flags = Array.isArray(p.flags) ? p.flags : [];
      const isSpecial = flags.includes(SPECIAL_FLAG as any);
      return {
        id: p.id,
        name: p.name,
        standardPrice: typeof p.price === "number" ? p.price : 0,
        isLocalAnesthesiaEligible: flags.includes(
          LOCAL_ANESTHESIA_ELIGIBLE_FLAG as any,
        ),
        isSpecial,
        disablePriceModifiers: flags.includes(
          DISABLE_PRICE_MODIFIERS_FLAG as any,
        ),
        // Risk-factor uplift applies to specials too (they only skip the
        // OR/surgeon overhead + the negotiable-fraction discount), so feed
        // the same percentage regardless of the special flag. (Zeroed by
        // the calculator when disablePriceModifiers is set.)
        percentageModifiers: autoModifierPct > 0 ? [autoModifierPct] : [],
      };
    });

    const hasSevenDay = addOns.includes("SEVEN_DAY_PACKAGE");
    const hasThreeDay = addOns.includes("THREE_DAY_PACKAGE");
    const hasAccessoriesAddOn = addOns.includes("ACCESSORIES");
    // Stay packages already include accessories — only charge for
    // standalone accessories when no stay is selected.
    const addons = [
      hasSevenDay
        ? { id: "SEVEN_DAY_PACKAGE", name: "7-day stay", price: ADDONS.SEVEN_DAY_PACKAGE }
        : null,
      hasThreeDay
        ? { id: "THREE_DAY_PACKAGE", name: "3-day stay", price: ADDONS.THREE_DAY_PACKAGE }
        : null,
      hasAccessoriesAddOn && !hasSevenDay && !hasThreeDay
        ? { id: "ACCESSORIES", name: "Accessories", price: ADDONS.ACCESSORIES }
        : null,
    ].filter((a): a is { id: string; name: string; price: number } => a !== null);

    return {
      procedures,
      addons,
      hasUploadedPhotos:
        hasUploadedPhotosOverride !== undefined
          ? hasUploadedPhotosOverride
          : fileList.length > 0,
    };
  };

  const getQuotePricing = (): QuotePricing => {
    return calculateQuotePricing(buildCalculatorInputs());
  };

  // Preview of the price the patient *would* see if photos were uploaded.
  // Used by the Photos-step welcome modal to surface the post-photo discount
  // before the user has uploaded anything.
  const getQuotePricingWithPhotos = (): QuotePricing => {
    return calculateQuotePricing(buildCalculatorInputs(true));
  };

  const getPatientDisplayRows = (): PatientDisplayRow[] => {
    const inputs = buildCalculatorInputs();
    const pricing = calculateQuotePricing(inputs);
    return groupPricingForPatientDisplay(inputs.procedures, pricing);
  };

  // Backwards-compatible shape for legacy callers that just need a single
  // total number. minPrice === maxPrice now — there is no range.
  const getSurgeonPricing = (): SurgeonPricing => {
    if (!selectedSurgeon || selectedProcedures.length === 0) {
      return { minPrice: undefined, maxPrice: undefined };
    }
    const total = getQuotePricing().total;
    return { minPrice: total, maxPrice: total };
  };

  const getSelectedProcedureTypePriceRanges = (): ProcedureTypePriceRange[] => {
    // Mirror the patient-facing catalog: exclude internal, window-hidden, AND
    // special-shadowed procedures so their prices don't skew the displayed
    // per-type range.
    const publicProcedures: Procedure[] = filterSpecialShadowed(
      (data.procedures || [])
        .filter((procedure: Procedure) => procedure.isInternal !== true)
        .filter((procedure: Procedure) => isWithinActiveWindow(procedure)),
    );

    return selectedProcedureTypes.map((procedureType) => {
      const prices = publicProcedures
        .filter((procedure) =>
          (procedure.procedureTypes || []).some(
            (pt) => pt.id === procedureType.id,
          ),
        )
        .map((procedure) => procedure.price)
        .filter(
          (price): price is number =>
            typeof price === "number" && Number.isFinite(price) && price > 0,
        );

      if (prices.length === 0) {
        return {
          procedureType,
          minPrice: undefined,
          maxPrice: undefined,
        };
      }

      return {
        procedureType,
        minPrice: Math.min(...prices),
        maxPrice: Math.max(...prices),
      };
    });
  };

  const getSimplifiedQuotePricing = (): SurgeonPricing => {
    const ranges = getSelectedProcedureTypePriceRanges();
    const minPrices = ranges
      .map((range) => range.minPrice)
      .filter(
        (price): price is number =>
          typeof price === "number" && Number.isFinite(price),
      );
    const maxPrices = ranges
      .map((range) => range.maxPrice)
      .filter(
        (price): price is number =>
          typeof price === "number" && Number.isFinite(price),
      );

    return {
      minPrice: minPrices.length > 0 ? Math.min(...minPrices) : undefined,
      maxPrice: maxPrices.length > 0 ? Math.max(...maxPrices) : undefined,
    };
  };

  const addSelectedProcedure = (procedure: Procedure) => {
    // if procedure is not in selecedProcedures, add it
    if (!selectedProcedures.some((p: Procedure) => p.id === procedure.id)) {
      const newSelectedProcedures = [...selectedProcedures, procedure];
      setSelectedProcedures(newSelectedProcedures);
      setSelectedProcedureTypesState([]);
    }
  };

  const removeSelectedProcedure = (procedure: Procedure) => {
    const newSelectedProcedures = selectedProcedures.filter(
      (p: Procedure) => p.id !== procedure.id,
    );
    setSelectedProcedures(newSelectedProcedures);
  };

  const clearSelectedProcedures = () => {
    setSelectedProcedures([]);
  };

  const setSelectedProcedureTypes = (procedureTypes: ProcedureType[]) => {
    setSelectedProcedureTypesState(procedureTypes);
    if (procedureTypes.length > 0) {
      setSelectedProcedures([]);
      setSelectedSurgeon(undefined);
    }
  };

  const updateSurgeon = (id: string) => {
    const surgeon = data.surgeons.find((surgeon: Surgeon) => surgeon.id === id);
    setSelectedSurgeon(surgeon);
  };

  const getAvailableSurgeons = (): Surgeon[] => {
    return data.surgeons.filter((surgeon: Surgeon) => {
      // Check if surgeon is available in the location
      const surgeonInThisLocation = surgeon.businessLocations.some(
        (loc) => loc.id === location,
      );

      if (!surgeonInThisLocation) return false;

      // Check if surgeon offers all selected procedures
      const notFoundProcedures = selectedProcedures.filter((proc) => {
        const found = proc.prices?.some((price: SurgeonProcedure) => {
          if (price.surgeon) {
            return price.surgeon.id === surgeon.id && price.price > 0;
          }
          return false;
        });
        return !found;
      });

      return notFoundProcedures.length === 0;
    });
  };

  useEffect(() => {
    // Skip auto-submit in resume mode - user should manually submit after editing
    if (incomingQuoteId) {
      if (process.env.REACT_APP_TARGET_ENV !== "production") {
        console.log(
          `[QuoteProvider] Skipping auto-submit in resume mode (quoteId: ${incomingQuoteId})`,
        );
      }
      return;
    }

    if (selectedSurgeon) {
      submitPersonalizedQuote();
    }
  }, [selectedSurgeon, incomingQuoteId]);

  // Mirror the active quote/draft id + A/B assignments onto first-party event
  // context so all tracked events get auto-tagged with quoteId (for funnel
  // JOINs) and the active test/variant pairs (so the A/B dashboard can build
  // per-test exposure + funnel-step curves). Wrapped — analytics tagging must
  // never break the funnel.
  useEffect(() => {
    let abTests: Array<{ testId: string; variant: string }> | undefined;
    try {
      abTests = abTestingService
        .getActiveVariants()
        .map((t) => ({ testId: t.testId, variant: t.variant }));
    } catch {
      abTests = undefined;
    }
    setEventContext({
      quoteId: quoteId || draftQuoteId || undefined,
      abTests,
    });
  }, [quoteId, draftQuoteId]);

  const updateDraftQuote = (payload: any) => {
    if (!draftQuoteId) {
      return;
    }

    // Refresh A/B assignments on this save. The ONLY caller today is the
    // contact-submitted save in submitPersonalizedQuote's onCompleted (fired
    // by StepPhotos's arrival effect) — there are NO per-step saves. That is
    // late enough for the current mid-funnel tests (financing-forward assigns
    // during the same StepPhotos render / at step 1 on desktop), so the draft
    // catches up before this fires; a future test assigning on a step the
    // user can abandon BEFORE photos-arrival will still miss its draft —
    // stamp the draft at assignment time if that case ever ships. Skipped
    // when empty so total storage loss can't wipe stored assignments (a
    // PARTIAL cookie loss still replaces the array with the surviving
    // subset — accepted, mirrors quote-stamp semantics).
    const activeABTests = abTestingService.getActiveVariants();

    commitMutation(environment, {
      mutation: UPDATE_DRAFT_QUOTE_MUTATION,
      variables: {
        input: {
          id: draftQuoteId,
          ...(activeABTests.length > 0 ? { activeABTests } : {}),
          ...payload,
        },
      },
      onError: (err) => console.error(err),
    });
  };

  const startDraftQuote = (options?: StartDraftQuoteOptions) => {
    if (draftQuoteId) {
      return Promise.resolve(draftQuoteId);
    }

    if (
      incomingQuoteId ||
      (selectedProcedures.length === 0 && selectedProcedureTypes.length === 0)
    ) {
      return Promise.resolve(null);
    }

    const now = new Date().toISOString();
    const activeABTests = abTestingService.getActiveVariants();
    const currentLanguage = i18n.language.includes("es")
      ? "spanish"
      : "english";

    const input = {
      selectedLocation: location,
      selectedProceduresV2: selectedProcedures.map((procedure) => procedure.id),
      selectedProcedureTypes: selectedProcedureTypes.map(
        (procedureType) => procedureType.id,
      ),
      selectedSurgeon: selectedSurgeon?.id,
      procedureDescription,
      leadOrigin,
      leadOriginFirst,
      leadOriginLast,
      leadOriginHistory,
      trackingIds,
      gclid,
      language: currentLanguage,
      lastStep: options?.lastStep || "procedures",
      lastStepAt: now,
      startedAt: now,
      activeABTests,
    };

    return new Promise<string | null>((resolve, reject) => {
      commitMutation(environment, {
        mutation: START_DRAFT_QUOTE_MUTATION,
        variables: {
          input,
        },
        onCompleted: (response: any) => {
          const newDraftId = response?.startDraftQuote?.id || "";
          if (newDraftId) {
            setDraftQuoteId(newDraftId);
            resolve(newDraftId);
            return;
          }
          resolve(null);
        },
        onError: (err) => {
          console.error(err);
          reject(err);
        },
      });
    });
  };

  const submitPersonalizedQuote = (options?: SubmitQuoteOptions) => {
    const fileListForMutation = fileList.map((file: any) => ({
      ...(file.name ? { name: file.name } : {}),
      ...(file.type ? { type: file.type } : {}),
      ...(file.url ? { url: file.url } : {}),
      response: { url: file.response?.url },
    }));

    // Collect active A/B tests
    const activeABTests = abTestingService.getActiveVariants();

    // Get current language from i18n
    const currentLanguage = i18n.language.includes("es")
      ? "spanish"
      : "english";

    const input = {
      ...(quoteId !== "" ? { id: quoteId } : {}),

      selectedLocation: location,
      selectedProceduresV2: selectedProcedures.map((procedure) => procedure.id),
      selectedProcedureTypes: selectedProcedureTypes.map(
        (procedureType) => procedureType.id,
      ),
      selectedSurgeon: selectedSurgeon?.id,
      // Procedure-type ranges still show a min/max during the simplified
      // flow (no specific procedures picked yet); once procedures are
      // selected, the calculator returns:
      //  - totalLowest    → minQuoted (attractive negotiation floor)
      //  - totalWithModifiers → maxQuoted (ceiling after risk uplift)
      minQuoted: Math.round(
        selectedProcedureTypes.length > 0
          ? getSimplifiedQuotePricing().minPrice || 0
          : getQuotePricing().totalLowest,
      ),
      maxQuoted: Math.round(
        selectedProcedureTypes.length > 0
          ? getSimplifiedQuotePricing().maxPrice || 0
          : getQuotePricing().totalWithModifiers,
      ),
      name,
      lastName,
      email,
      phone,
      zipCode,
      weight,
      heightFt,
      heightIn,
      age,
      hasMedicalConditions,
      smoker,
      priorCosmeticSurgery,
      medicalConditions,
      lost50pounds,
      patientReadiness,
      surgeryDateRange,
      budget,
      willingToTravel,
      patientNotes,
      fileList: fileListForMutation,
      cherryFinancingFile: cherryFinancingFile
        ? { response: { url: cherryFinancingFile.response?.url } }
        : null,
      addOns,
      acceptedHipaa: hipaaAgreement,
      procedureDescription,
      otherMedicalConditionDescription,
      priorCosmeticSurgeryDescription,
      optInText,
      leadOrigin,
      leadOriginFirst,
      leadOriginLast,
      leadOriginHistory,
      trackingIds,
      // Merged TrackingTouch journal — cookie ∪ localStorage, deduped per
      // submit. Server appends to Quote.touchHistory + Patient.touchHistory
      // (cross-quote deduped) in a transaction.
      touches: readTouches(),
      gclid,
      language: currentLanguage,
      ...(quoteId === "" && QUOTE_VERSION ? { version: QUOTE_VERSION } : {}),
      ...(draftQuoteId ? { draftQuoteId } : {}),
      activeABTests,
      // Only send deviceTimezone on new quotes (not updates) — it's used once
      // to set Patient.timezone and irrelevant on subsequent saves.
      ...(quoteId === ""
        ? { deviceTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone }
        : {}),
    };

    if (quoteId === "" && email === "") {
      return;
    }

    const commit = (recaptchaToken: string | null) =>
      commitMutation(environment, {
        mutation: quoteId !== "" ? UPDATE_QUOTE_MUTATION : ADD_QUOTE_MUTATION,
        variables: {
          input: recaptchaToken ? { ...input, recaptchaToken } : input,
        },
        onCompleted: (response: any) => {
          // console.log("Response received from server:", response);
          const resolvedQuoteId = response.addQuote?.id || quoteId;
          if (response.addQuote !== undefined) {
            setQuoteId(response.addQuote.id);
            setQuoteStatus("NEW_LEAD");
            // Track conversion when new lead is stored in database
            trackQuoteCompletedOnce();
            // Fire-and-forget: stamp gift pass attribution on the quote.
            // Never blocks or alerts on failure.
            if (giftPass?.code) {
              fetch(`${process.env.REACT_APP_API_URL || ""}/api/booking-pass/attach`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ code: giftPass.code, quoteId: response.addQuote.id }),
              }).catch(() => {});
            }
          }

          if (options?.sendSuccessEmail && draftQuoteId) {
            updateDraftQuote({
              finalQuoteId: resolvedQuoteId,
              contactSubmittedAt: new Date().toISOString(),
              lastStep: "contact_submitted",
              lastStepAt: new Date().toISOString(),
            });
          }
          if (options?.sendSuccessEmail && resolvedQuoteId) {
            fetch(`${process.env.PUBLIC_URL}/api/send-email-success`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ quoteId: resolvedQuoteId }),
            })
              .then((res) => {
                if (!res.ok) {
                  console.error(
                    `Email send failed with status ${res.status}`,
                    res,
                  );
                  return res.text().then((text) => {
                    console.error("Email error response:", text);
                  });
                }
                if (process.env.REACT_APP_TARGET_ENV !== "production") {
                  console.log("Email sent successfully");
                }
              })
              .catch((err) => {
                console.error("Email fetch error:", err);
              });
          }

          if (
            options?.redirectOnComplete &&
            selectedSurgeon?.id !== "64f7fac0f5661576d884ff89" &&
            selectedSurgeon?.id !== "64e6102a6c9456cc2fa181f2"
          ) {
            window.location.href =
              "https://calendly.com/drmiami-group-info/15-minute-free-consultation";
          }
        },
        onError: (err: any) => console.error(err),
      });

    // Bot-score Phase 1 (2026-09-13): mint a reCAPTCHA v3 token on the
    // LEAD-CREATING submit only (quoteId === "" → addQuote). Subsequent
    // updateQuote saves aren't lead creations and stay untouched.
    //
    // ⚠️ FAIL-OPEN, on both ends: executeFunnelRecaptcha never rejects and
    // caps itself at 2s, `recaptchaToken` is an OPTIONAL input the server
    // treats as advisory, and a null token simply submits without the field.
    // There is no branch here in which the lead does not go out.
    if (quoteId === "") {
      executeFunnelRecaptcha().then(
        (token) => commit(token),
        () => commit(null),
      );
    } else {
      commit(null);
    }
  };

  const shownProcedures = filterSpecialShadowed(
    data.procedures
      .filter((procedure: any) => !procedure.isInternal)
      // Scheduled active window — hide procedures outside their visibility window.
      .filter((procedure: any) => isWithinActiveWindow(procedure))
      .filter((procedure: any) => {
        if (gender) {
          return procedure.flags.some((flag: any) => flag === gender);
        }
        return true;
      }),
    // MWL flag-filter removed 2026-09 — it compared against a value
    // ("LOST_50_POUNDS") the enum never contained, so it never matched;
    // MASSIVE_WEIGHT_LOSS remains visibility-metadata only (deprecated).
  );

  // Catalog before the gender filter. Campaign-focus resolution
  // (useCampaignFocus) must run against this: gender defaults to FEMALE, so
  // resolving against shownProcedures would veto male-procedure campaign
  // links before the visitor ever gets a chance to be enrolled. Still honors
  // the active window so a window-hidden procedure can't be focus-resolved —
  // and, same rule, special-of shadowing is applied here too: a campaign-focus
  // link pointed at a currently-shadowed BASE procedure intentionally does not
  // resolve, so the visitor falls back to the default catalog, where the
  // special (pinned + badged) is shown instead.
  const proceduresAllGender = filterSpecialShadowed(
    data.procedures
      .filter((procedure: any) => !procedure.isInternal)
      .filter((procedure: any) => isWithinActiveWindow(procedure)),
  );

  const saveFilesToQuote = (updatedFileList: any[], updatedCherryFile?: any) => {
    if (!quoteId) return;
    const fileListForMutation = updatedFileList.map((file: any) => ({
      ...(file.name ? { name: file.name } : {}),
      ...(file.type ? { type: file.type } : {}),
      ...(file.url ? { url: file.url } : {}),
      response: { url: file.response?.url },
    }));
    const input: any = {
      id: quoteId,
      fileList: fileListForMutation,
    };
    if (updatedCherryFile !== undefined) {
      input.cherryFinancingFile = updatedCherryFile
        ? {
            ...(updatedCherryFile.name ? { name: updatedCherryFile.name } : {}),
            ...(updatedCherryFile.directUrl ? { directUrl: updatedCherryFile.directUrl } : {}),
            response: { url: updatedCherryFile.response?.url },
          }
        : null;
    }

    // Recompute minQuoted / maxQuoted against the *just-uploaded* file
    // list — `fileList` React state isn't flushed yet at this point, so
    // we pass the override explicitly. The photo unlock drops totalLowest
    // by ~25% of the procedure portion; without this write the DB
    // minQuoted stays at the pre-upload (higher) value.
    if (selectedProcedures.length > 0) {
      const pricing = calculateQuotePricing(
        buildCalculatorInputs(updatedFileList.length > 0),
      );
      input.minQuoted = Math.round(pricing.totalLowest);
      input.maxQuoted = Math.round(pricing.totalWithModifiers);
    }

    commitMutation(environment, {
      mutation: UPDATE_QUOTE_MUTATION,
      variables: { input },
      onCompleted: () => {
        console.log("[QuoteProvider] Files saved to quote");
      },
      onError: (err) => {
        console.error("[QuoteProvider] Error saving files:", err);
      },
    });
  };

  // Best-effort intent signals — stamp the quote so coordinators can see the
  // patient acted. Fire-and-forget; never block the flow or surface errors to
  // the patient (a back-office breadcrumb, not part of the funnel). Neither
  // implies booked/approved.
  const markFinancingApplyClicked = (provider: string) => {
    if (!quoteId) return;
    commitMutation(environment, {
      mutation: MARK_FINANCING_APPLY_CLICKED_MUTATION,
      variables: { quoteId, provider },
      onError: (err) => {
        console.error("[QuoteProvider] markFinancingApplyClicked failed:", err);
      },
    });
  };

  const markBookNowPressed = () => {
    if (!quoteId) return;
    commitMutation(environment, {
      mutation: MARK_BOOK_NOW_PRESSED_MUTATION,
      variables: { quoteId },
      onError: (err) => {
        console.error("[QuoteProvider] markBookNowPressed failed:", err);
      },
    });
  };

  const markEvaluationRequested = () => {
    if (!quoteId) return;
    commitMutation(environment, {
      mutation: MARK_EVALUATION_REQUESTED_MUTATION,
      variables: { quoteId },
      onError: (err) => {
        console.error("[QuoteProvider] markEvaluationRequested failed:", err);
      },
    });
  };

  const value = {
    quoteId,
    quoteStatus,
    draftQuoteId,
    selectedProcedures,
    addSelectedProcedure,
    removeSelectedProcedure,
    selectedSurgeon,
    selectedProcedureTypes,
    setSelectedProcedureTypes,
    clearSelectedProcedures,
    gender,
    setGender,
    email,
    setEmail,
    phone,
    setPhone,
    zipCode,
    setZipCode,
    name,
    setName,
    lastName,
    setLastName,
    age,
    setAge,
    weight,
    setWeight,
    heightFt,
    heightIn,
    setHeightFt,
    setHeightIn,
    hasMedicalConditions,
    setHasMedicalConditions,
    medicalConditions,
    setMedicalConditions,
    smoker,
    setSmoker,
    priorCosmeticSurgery,
    setPriorCosmeticSurgery,
    lost50pounds,
    setLost50Pounds,
    patientReadiness,
    setPatientReadiness,
    surgeryDateRange,
    setSurgeryDateRange,
    budget,
    setBudget,
    willingToTravel,
    setWillingToTravel,
    patientNotes,
    setPatientNotes,
    location,
    setLocation,
    locationOptions,
    procedures: shownProcedures,
    proceduresAllGender,
    surgeons: data.surgeons,
    procedureTypes: data.procedureTypes.filter(
      (procedureType: any) => !procedureType.isInternal,
    ),
    proceduresWithSurgeon: data.proceduresWithSurgeon,
    getSurgeonPricing,
    getQuotePricing,
    getQuotePricingWithPhotos,
    getPatientDisplayRows,
    getSelectedProcedureTypePriceRanges,
    getAvailableSurgeons,
    updateSurgeon,
    submitPersonalizedQuote,
    startDraftQuote,
    fileList,
    setFileList,
    cherryFinancingFile,
    setCherryFinancingFile,
    saveFilesToQuote,
    markFinancingApplyClicked,
    markBookNowPressed,
    markEvaluationRequested,

    // Personalization
    addOns,
    setAddOns,
    toggleAddOn,

    //skipped personal details
    skippedPersonalDetails,
    setSkippedPersonalDetails,

    //hipaa agreement
    hipaaAgreement,
    setHipaaAgreement,

    // descriptions
    procedureDescription,
    setProcedureDescription,
    otherMedicalConditionDescription,
    setOtherMedicalConditionDescription,
    priorCosmeticSurgeryDescription,
    setPriorCosmeticSurgeryDescription,

    //simple medical history
    simpleMedicalHistory,
    setSimpleMedicalHistory,

    //reloaded quote
    quoteReloaded,
    setQuoteReloaded,
    quoteShared,
    setQuoteShared,
    isLoading,

    optInText,
    setOptInText,

    leadOrigin,
    leadOriginFirst,
    leadOriginLast,
    leadOriginHistory,
    trackingIds,

    // Promo code
    appliedPromoCode,
    setAppliedPromoCode,

    // Friends & Family Pass
    giftPass,
    giftPassDead,
    clearGiftPass,
  };

  useEffect(() => {
    // Prevent duplicate loads for the same quote
    if (quoteLoadedRef.current === incomingQuoteId) {
      console.log("[QuoteProvider] Skipping restore - already loaded:", incomingQuoteId);
      return;
    }
    console.log("[QuoteProvider] Restore effect running, incomingQuoteId:", incomingQuoteId, "data.quote:", !!data.quote);

    // returning with preset quoteId
    if (incomingQuoteId && data.quote) {
      console.log("[QuoteProvider] Restoring quote data:", {
        quoteId: data.quote.id,
        fileListCount: data.quote.fileList?.length,
        hasCherryFile: !!data.quote.cherryFinancingFile,
        simpleStatus: data.quote.simpleStatus,
        selectedLocation: data.quote.selectedLocation?.id,
        selectedSurgeon: data.quote.selectedSurgeon?.id,
        procedureCount: data.quote.selectedProceduresV2?.length,
      });
      //find the location by the data.quote.selectedLocation.id in data.businessLocations and select it

      if (data.quote.selectedLocation) {
        const location = data.businessLocations.find(
          (location: any) => location.id === data.quote.selectedLocation.id,
        );
        if (location) {
          setLocation(location.id);
        }
      }

      //find the procedures by the data.quote.selectedProceduresV2.id in data.procedures and select it

      if (data.quote.selectedProceduresV2 !== undefined) {
        if (data.quote.selectedProceduresV2.length > 0) {
          const procedures = data.procedures.filter((procedure: any) =>
            data.quote.selectedProceduresV2.some(
              (selectedProcedure: any) => selectedProcedure.id === procedure.id,
            ),
          );

          if (procedures.length > 0) {
            setSelectedProcedures(procedures);
          }
        }
      }

      if (data.quote.selectedProcedureTypes !== undefined) {
        if (data.quote.selectedProcedureTypes.length > 0) {
          const procedureTypes = data.procedureTypes.filter(
            (procedureType: any) =>
              data.quote.selectedProcedureTypes.some(
                (selectedProcedureType: any) =>
                  selectedProcedureType.id === procedureType.id,
              ),
          );

          if (procedureTypes.length > 0) {
            setSelectedProcedureTypesState(procedureTypes);
          }
        }
      }

      if (data.quote.selectedSurgeon) {
        const surgeon = data.surgeons.find(
          (surgeon: any) => surgeon.id === data.quote.selectedSurgeon.id,
        );

        if (surgeon) {
          setSelectedSurgeon(surgeon);
        }
      }

      setQuoteId(data.quote.id);
      const isReturningFromBooking = searchParams.get("booking") === "success";
      setQuoteStatus(isReturningFromBooking ? "BOOKED" : (data.quote.simpleStatus || null));

      // Persist BOOKED status to DB when returning from successful Stripe checkout
      if (isReturningFromBooking && data.quote.simpleStatus !== "BOOKED") {
        commitMutation(environment, {
          mutation: UPDATE_QUOTE_MUTATION,
          variables: {
            input: {
              id: data.quote.id,
              simpleStatus: "BOOKED",
            },
          },
          onCompleted: () => {
            console.log("[QuoteProvider] BOOKED status persisted to DB");
          },
          onError: (err) => {
            console.error("[QuoteProvider] Error persisting BOOKED status:", err);
          },
        });
      }
      setEmail(data.quote.email);
      setPhone(data.quote.phone);
      setZipCode(data.quote.zipCode);
      setName(data.quote.name);
      setLastName(data.quote.lastName);
      setAge(data.quote.age ? data.quote.age.toString() : "");
      setWeight(data.quote.weight ? data.quote.weight.toString() : "");
      setHeightFt(data.quote.heightFt ? data.quote.heightFt.toString() : "");
      setHeightIn(data.quote.heightIn ? data.quote.heightIn.toString() : "");
      setSmoker(data.quote.smoker);
      setPriorCosmeticSurgery(data.quote.priorCosmeticSurgery);
      setHipaaAgreement(data.quote.acceptedHipaa);

      setAddOns((data.quote.addOns ?? []) as QuoteAddOn[]);
      setMedicalConditions(data.quote.medicalConditions);

      // Restore the language the quote was submitted in
      if (data.quote.language === "spanish") {
        i18n.changeLanguage("es");
      }

      // Ensure each file has a uid for GraphQL mutation compatibility
      const filesWithUids = (data.quote.fileList || []).map(
        (file: any, index: number) => ({
          ...file,
          uid: file.uid || `db-file-${index}-${Date.now()}`,
        }),
      );
      setFileList(filesWithUids);

      if (data.quote.cherryFinancingFile) {
        setCherryFinancingFile({
          ...data.quote.cherryFinancingFile,
          uid: data.quote.cherryFinancingFile.uid || `cherry-${Date.now()}`,
        });
      }

      console.log("[QuoteProvider] Restored fileList:", filesWithUids.length, "files, cherryFile:", !!data.quote.cherryFinancingFile, "status:", data.quote.simpleStatus);

      setProcedureDescription(data.quote.procedureDescription);
      setOptInText(data.quote.optInText);
      if (data.quote.patientReadiness) setPatientReadiness(data.quote.patientReadiness);
      if (data.quote.surgeryDateRange) setSurgeryDateRange(data.quote.surgeryDateRange);
      if (data.quote.budget) setBudget(data.quote.budget);
      if (data.quote.willingToTravel) setWillingToTravel(data.quote.willingToTravel);
      if (data.quote.patientNotes) setPatientNotes(data.quote.patientNotes);

      // Mark this quote as loaded to prevent duplicate loads
      quoteLoadedRef.current = incomingQuoteId;
      setQuoteReloaded(true);
    }
  }, [incomingQuoteId]); // Only run when quoteId changes, not on every data update

  useEffect(() => {
    if (data) {
      setIsLoading(false);
    }
  }, []);

  return (
    <QuoteContext.Provider value={value}>{children}</QuoteContext.Provider>
  );
};
